import * as React from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { forEach, isEmpty, map, some, truncate } from 'lodash-es';
import {
    CaretDownMaterialIcon,
    DateDropdown,
    DesktopIcon,
    DetailedTable,
    DetailedTableHeadersProps,
    MagnifyingGlassIcon,
    OtherDevicesIcon,
    palette,
    PhoneIcon,
    SecondaryButton,
    SimplePieChart,
    SimplePopover,
    TabletIcon,
    Typography,
} from '@dev/ee-components';
import { Channel, DeviceFormFactor } from '@dev/ee-api/ee-api-models';
import { ArrowBackIos as ArrowBackIosIcon } from '@material-ui/icons';

import {
    StartAllCampaignsLoading,
    StartAllChannelsLoading,
    StartUserAgentChannelLoading,
    StartUserAgentLoading,
} from '@/store/activity/devices/actions';
import { ApplicationState as State } from '@/store/index';
import { ExportsPaths } from '@/services/dictionaries/Paths';

import { DevicesProps, DispatchProps, Props } from './DeviceDetailsProps';

const mapStateToProps = (state: State, ownProps: DevicesProps): DevicesProps => ({
    ...ownProps,
    loading: state.activity.devices.loading,
    userAgent: state.activity.devices.userAgent,
    campaigns: state.activity.locations.campaigns,
    channels: state.activity.locations.channels,
});

const dispatchProps = {
    StartUserAgentLoading,
    StartAllChannelsLoading,
    StartAllCampaignsLoading,
    StartUserAgentChannelLoading,
};

const Nodatacontainer = styled.header`
    min-height: 50rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    @media only screen and (max-width: 1400px) {
        min-height: 30rem;
    }
`;

const linkStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '16px',
    width: '20rem',
};

const graphContainer = {
    width: '50rem',
    margin: '0 auto 5rem',
};

const tableContainer = {
    marginBottom: '2.4rem',
    background: '#fff',
    paddingTop: '2rem',
};

const emptyDataScreen = {
    textAlign: 'center' as const,
    color: palette.jacarta[5],
};

class DeviceDetails extends React.Component<Props> {
    public state = {
        from: null,
        to: null,
        channelValue: null,
        campaignValue: null,
        selectedChannel: null,
        dateRangeValue: this.props.t('daterange'),
    };

    public componentDidMount(): void {
        this.props.StartAllCampaignsLoading();
        this.props.StartAllChannelsLoading();

        this.loadUserAgentData();
    }

    private loadUserAgentData = (): void => {
        this.props.StartUserAgentLoading({ from: this.state.from, to: this.state.to });
    };

    private calculatePercentage = (totalCount, count): string => {
        const percentage = (count / totalCount) * 100;
        if (percentage % 2 === 0) {
            return `${percentage.toFixed(0)}%`;
        } else {
            return `${percentage.toFixed(2)}%`;
        }
    };

    private populateRows = (object): Record<string, any> => {
        let rows = {};
        let otherTotalCount = 0;
        let desktopTotalCount = 0;
        let mobileTotalCount = 0;
        let tabletTotalCount = 0;

        forEach(object, (category): void => {
            forEach(category.Items, (subRowData): number => {
                switch (category.FormFactor) {
                    case DeviceFormFactor.Other:
                        return (otherTotalCount += subRowData.OpenCount);
                    case DeviceFormFactor.Mobile:
                        return (mobileTotalCount += subRowData.OpenCount);
                    case DeviceFormFactor.Tablet:
                        return (tabletTotalCount += subRowData.OpenCount);
                    case DeviceFormFactor.Desktop:
                        return (desktopTotalCount += subRowData.OpenCount);
                }
            });
        });

        const totalCount = otherTotalCount + desktopTotalCount + mobileTotalCount + tabletTotalCount;
        const devicesCategoryObj = {
            otherTotalCount,
            desktopTotalCount,
            mobileTotalCount,
            tabletTotalCount,
            totalCount,
        };

        rows = this.createRows(object, devicesCategoryObj);
        const graphData = this.createGraphData(devicesCategoryObj);

        return { rows, graphData };
    };

    private createRows = (data, devicesCategoryObj): Record<string, any> =>
        map(data, (row): Record<string, any> => {
            let color;
            let rate;
            let opens;
            let details;

            const { totalCount, desktopTotalCount, mobileTotalCount, tabletTotalCount, otherTotalCount } =
                devicesCategoryObj;

            switch (row.FormFactor) {
                case DeviceFormFactor.Other:
                    opens = otherTotalCount;
                    color = palette.indigo[2];
                    rate = this.calculatePercentage(totalCount, otherTotalCount);
                    details = this.createSubRows(row, totalCount);
                    break;
                case DeviceFormFactor.Mobile:
                    opens = mobileTotalCount;
                    color = palette.indigo[6];
                    rate = this.calculatePercentage(totalCount, mobileTotalCount);
                    details = this.createSubRows(row, totalCount);
                    break;
                case DeviceFormFactor.Tablet:
                    opens = tabletTotalCount;
                    color = palette.indigo[4];
                    rate = this.calculatePercentage(totalCount, tabletTotalCount);
                    details = this.createSubRows(row, totalCount);
                    break;
                case DeviceFormFactor.Desktop:
                    opens = desktopTotalCount;
                    color = palette.indigo[8];
                    rate = this.calculatePercentage(totalCount, desktopTotalCount);
                    details = this.createSubRows(row, totalCount);
                    break;
            }

            return {
                id: row.FormFactorName,
                name: row.FormFactorName,
                opens,
                rate,
                color,
                details,
            };
        });

    private createSubRows = (row, totalCount): Record<string, any> =>
        map(
            row.Items,
            (subrow): Record<string, any> => ({
                name: subrow.Name,
                opens: subrow.OpenCount,
                rate: this.calculatePercentage(totalCount, subrow.OpenCount),
            })
        );

    private createGraphData = (devicesCategoryObj): Record<string, any> => [
        {
            name: 'Desktop',
            value: devicesCategoryObj.desktopTotalCount,
            color: palette.indigo[8],
            icon: <DesktopIcon />,
        },
        {
            name: 'Mobile',
            value: devicesCategoryObj.mobileTotalCount,
            color: palette.indigo[6],
            icon: <PhoneIcon />,
        },
        {
            name: 'Tablet',
            value: devicesCategoryObj.tabletTotalCount,
            color: palette.indigo[4],
            icon: <TabletIcon />,
        },
        {
            name: 'Other',
            value: devicesCategoryObj.otherTotalCount,
            color: palette.indigo[2],
            icon: <OtherDevicesIcon />,
        },
    ];

    private handleChannelChange(): void {
        this.props.StartUserAgentChannelLoading({
            name: this.state.selectedChannel,
            from: this.state.from,
            to: this.state.to,
        });
    }

    private createChannelsArray = (object): Channel[] => {
        const array = [];
        map(object, (channel): void => {
            if (channel.Name.length > 30) {
                channel.Label = truncate(channel.Name, {
                    length: 30,
                });
            }
            array.push({ value: channel.Name, label: channel.Label || channel.Name, key: channel.Name });
        });
        array.unshift({ value: 'all', label: 'All', borderBottom: true, key: 'all' });
        return array;
    };

    private createTableHeader = (): DetailedTableHeadersProps[] => {
        const { t } = this.props;

        return [
            {
                id: 'name',
                numeric: false,
                label: t('devicedetails'),
                disableSort: true,
            },
            {
                id: 'opens',
                numeric: true,
                label: t('opens'),
                disableSort: true,
            },
            {
                id: 'percentage',
                numeric: true,
                label: '',
            },
        ];
    };

    private renderDateRange = (): JSX.Element => {
        const { t } = this.props;
        const dateRangeData = [
            {
                label: t('last7days'),
                value: '7',
                borderBottom: true,
            },
            {
                label: t('last14days'),
                value: '14',
            },
            {
                label: t('last21days'),
                value: '21',
            },
            {
                label: t('specificdaterange'),
                value: 'calendar',
            },
        ];

        const runOnSelectedChannel = this.state.selectedChannel ? this.handleChannelChange : this.loadUserAgentData;

        return (
            <DateDropdown
                onItemClick={(e, obj): void => {
                    const dateValue = obj.value;
                    const to = moment.utc().set({ hour: 0, minute: 0, second: 0 }).format();
                    let from = null;

                    if (dateValue !== 'calendar') {
                        from = moment().subtract(dateValue, 'd').utc().set({ hour: 0, minute: 0, second: 0 }).format();

                        this.setState({ from, to, dateRangeValue: obj.label }, (): void => {
                            runOnSelectedChannel();
                        });
                    }
                }}
                type="custom"
                options={dateRangeData}
                calendarOnChange={(start, end): void => {
                    const from = start.utc().set({ hour: 0, minute: 0, second: 0 }).format();
                    const to = end.utc().set({ hour: 0, minute: 0, second: 0 }).format();
                    const dateFromPlaceholder = start.utc().format('DD MMM YYYY');
                    const dateToPlaceholder = end.utc().format('DD MMM YYYY');

                    const dateRangeValue = `${dateFromPlaceholder} - ${dateToPlaceholder}`;

                    this.setState({ from, to, dateRangeValue }, (): void => {
                        runOnSelectedChannel();
                    });
                }}
                popperProps={{ disablePortal: true, container: document.getElementById('app-root') }}
            >
                <SecondaryButton
                    text={this.state.dateRangeValue}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </DateDropdown>
        );
    };

    private renderCampaignsSelect = (campaigns): JSX.Element => {
        const { t } = this.props;

        return (
            <SimplePopover
                type={'custom'}
                listItemWidth={'200px'}
                options={campaigns}
                onItemClick={(e, obj): void => {
                    const selectedChannel = obj.value;

                    if (selectedChannel === 'all') {
                        this.setState(
                            {
                                selectedChannel: null,
                                campaignValue: null,
                                channelValue: null,
                            },
                            (): void => {
                                this.loadUserAgentData();
                            }
                        );
                    } else {
                        this.setState(
                            {
                                selectedChannel,
                                campaignValue: selectedChannel,
                                channelValue: null,
                            },
                            (): void => {
                                this.handleChannelChange();
                            }
                        );
                    }
                }}
                maxHeight={600}
            >
                <SecondaryButton
                    text={t('campaigns')}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </SimplePopover>
        );
    };

    private renderChannelsSelect = (channels): JSX.Element => {
        const { t } = this.props;

        return (
            <SimplePopover
                type={'custom'}
                listItemWidth={'150px'}
                options={channels}
                onItemClick={(e, obj): void => {
                    const selectedChannel = obj.value;
                    if (selectedChannel === 'all') {
                        this.setState(
                            {
                                selectedChannel: null,
                                campaignValue: null,
                                channelValue: null,
                            },
                            (): void => {
                                this.loadUserAgentData();
                            }
                        );
                    } else {
                        this.setState(
                            {
                                selectedChannel,
                                campaignValue: null,
                                channelValue: selectedChannel,
                            },
                            (): void => {
                                this.handleChannelChange();
                            }
                        );
                    }
                }}
                popperProps={{ disablePortal: true, popperOptions: { positionFixed: true } }}
            >
                <SecondaryButton
                    text={t('channels')}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </SimplePopover>
        );
    };

    private renderLinksDropdown = (): JSX.Element => {
        const { t } = this.props;
        return (
            <div style={{ marginTop: '0' }} className="dropdown">
                <button
                    type="button"
                    className="btn btn_md btn_primary btn_caret"
                    style={{ height: '4.4rem' }}
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    <span id="emorereports">{t('view')}</span>
                </button>
                <ul className="dropdown-menu drop-sm">
                    <li>
                        <Link to="/activity/progress">{t('startinprogress')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/emails">{t('emaillogs')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/bouncedetails">{t('bouncedetails')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/geography">{t('geography')}</Link>
                    </li>
                    <li>
                        <Link to={ExportsPaths.Exports}>{t('exporthistory')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/notifications">{t('notificationlog')}</Link>
                    </li>
                </ul>
            </div>
        );
    };

    public render(): JSX.Element {
        const { t } = this.props;
        let userAgentData;
        const isData = some(this.props.userAgent, (obj): boolean => !isEmpty(obj.Items));
        const headers = this.createTableHeader();

        if (this.props.userAgent) {
            userAgentData = this.populateRows(this.props.userAgent);
        }

        let campaigns = [];
        let channels = [];
        if (this.props.channels) {
            channels = this.createChannelsArray(this.props.channels);
        }
        if (this.props.campaigns) {
            campaigns = this.createChannelsArray(this.props.campaigns);
        }

        return (
            <>
                <Link style={linkStyles} to="/activity">
                    <ArrowBackIosIcon style={{ width: '9px', height: '12px' }} />
                    {t('activity')}
                </Link>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                    <Typography variant="h1">{t('devicedetails')}</Typography>
                    <div style={{ display: 'flex' }}>
                        {this.renderDateRange()}
                        {campaigns.length ? (
                            <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                                {this.renderCampaignsSelect(campaigns)}
                            </div>
                        ) : null}
                        {channels.length ? (
                            <div style={{ marginRight: '5px' }}>{this.renderChannelsSelect(channels)}</div>
                        ) : null}
                        {this.renderLinksDropdown()}
                    </div>
                </div>
                <div style={{ minHeight: '3rem' }}>
                    {this.state.campaignValue || this.state.channelValue ? (
                        <Typography variant={'h2'}>{this.state.campaignValue || this.state.channelValue}</Typography>
                    ) : null}
                </div>
                <div>
                    {!isData && this.props.userAgent ? (
                        <Nodatacontainer>
                            <MagnifyingGlassIcon />
                            <p style={emptyDataScreen}>{t('nouseragentsdata')}</p>
                        </Nodatacontainer>
                    ) : null}
                    {isData ? (
                        <div style={graphContainer}>
                            <SimplePieChart
                                data={userAgentData.graphData}
                                height={540}
                                width={450}
                                showPercentage={true}
                                hideLabels={true}
                                outerRadius={200}
                                innerRadius={150}
                            />
                        </div>
                    ) : null}
                    {isData ? (
                        <div style={tableContainer}>
                            <DetailedTable rows={userAgentData.rows} headers={headers} />
                        </div>
                    ) : null}
                </div>
            </>
        );
    }
}

export default compose(
    withTranslation(),
    connect<DevicesProps, DispatchProps>(mapStateToProps, dispatchProps)
)(DeviceDetails);
